/**
 * Group-by rendering for the Agents console's ticket list (TRUS-687): one
 * collapsible section per group (org, origin, verdict, …) with its count, the
 * first N rows the server sent, and "Show more in this group", which narrows the
 * list to that group (or, for an axis with no filter such as Epic, asks for more
 * rows per group).
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { tvTokens } from '../../../shared/components/triveny';
import { GROUP_BY_LABEL, type RcaGroup, type RcaGroupBy } from './rcaFilterModel';
import { linkButton } from './rcaStyles';

export function RcaGroupSections<T>({
  groupBy,
  groups,
  renderRow,
  onShowMore,
}: {
  groupBy: RcaGroupBy;
  groups: RcaGroup<T>[];
  renderRow: (item: T) => ReactNode;
  onShowMore: (group: RcaGroup<T>) => void;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  if (groups.length === 0) {
    return <p style={{ padding: '18px 16px', margin: 0, fontSize: 13, color: tvTokens.ink400 }}>No tickets match these filters.</p>;
  }
  return (
    <>
      {groups.map((g) => {
        const isCollapsed = !!collapsed[g.key];
        const more = g.count - g.items.length;
        return (
          <section key={g.key} data-testid={`rca-group-${g.key}`} aria-label={`${GROUP_BY_LABEL[groupBy]}: ${g.label}`}>
            <button
              type="button"
              onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
              aria-expanded={!isCollapsed}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '8px 16px',
                border: 'none',
                borderTop: `1px solid ${tvTokens.border}`,
                background: tvTokens.paper2,
                color: tvTokens.ink700,
                fontSize: 12.5,
                fontWeight: 700,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              {isCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.label}</span>
              <span style={{ fontFamily: tvTokens.fontMono, fontSize: 11, fontWeight: 600, color: tvTokens.ink400, fontVariantNumeric: 'tabular-nums' }}>{g.count}</span>
            </button>
            {!isCollapsed && (
              <>
                {g.items.map((it) => renderRow(it))}
                {more > 0 && (
                  <div style={{ padding: '6px 16px 10px', borderTop: `1px solid ${tvTokens.borderMuted}` }}>
                    <button type="button" onClick={() => onShowMore(g)} style={linkButton}>
                      Show more in this group ({more} more)
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        );
      })}
    </>
  );
}
